// src/services/contracts.js
// PGT Ecosystem — Shared Web3 Contract Service
// Direct contract interaction from any frontend. No server dependency.
// Works with ethers v6. Supports read-only (provider) and write (signer) modes.

import { ethers } from 'ethers';

// ---------------------------------------------------------------------------
// Environment helpers
// ---------------------------------------------------------------------------

function env(name) {
  // Vite
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      if (import.meta.env[`VITE_${name}`]) return import.meta.env[`VITE_${name}`];
      if (import.meta.env[name]) return import.meta.env[name];
    }
  } catch (_) { /* ignore */ }
  // CRA / Node
  if (typeof process !== 'undefined' && process.env) {
    return process.env[`VITE_${name}`] || process.env[`REACT_APP_${name}`] || process.env[name] || '';
  }
  return '';
}

// ---------------------------------------------------------------------------
// Chain config
// ---------------------------------------------------------------------------

const CHAINS = {
  8453: {
    name: 'Base',
    rpcUrl: 'https://mainnet.base.org',
    explorer: 'https://basescan.org',
  },
  84532: {
    name: 'Base Sepolia',
    rpcUrl: 'https://sepolia.base.org',
    explorer: 'https://sepolia.basescan.org',
  },
};

function getChainId() {
  return Number(env('CHAIN_ID') || env('APP_CHAIN_ID') || 84532);
}

function getRpcUrl() {
  return env('RPC_URL') || env('BASE_RPC_URL') || env('BASE_SEPOLIA_RPC_URL') || CHAINS[getChainId()]?.rpcUrl || 'https://sepolia.base.org';
}

// ---------------------------------------------------------------------------
// Contract addresses
// ---------------------------------------------------------------------------

export function getAddresses() {
  return {
    pgtToken:                env('PGT_TOKEN_ADDRESS') || env('PGT_ADDRESS') || '0x907Ade9501c616151eCB5188423796324F05202C',
    eventTicket:             env('EVENT_TICKET_ADDRESS') || env('CONTRACT_ADDRESS') || '0x31fb6b252A4140988EFd67bf84727dFD2B1d2721',
    eventTicketWithEvents:   env('EVENT_TICKET_WITH_EVENTS_ADDRESS') || '0x31fb6b252A4140988EFd67bf84727dFD2B1d2721',
    marketplace:             env('MARKETPLACE_ROUTER') || env('MARKETPLACE_ADDRESS') || '0x2F25AFd1C4bD7c3134e7859788e01968483417f7',
    marketplaceCore:         env('MARKETPLACE_CORE') || env('MARKETPLACE_CORE_ADDRESS') || '0xC629398a3A3Cff334F2629159DA9da834Dc35557',
    simpleSwap:              env('SIMPLE_SWAP_ADDRESS') || env('REACT_APP_SIMPLE_SWAP_ADDRESS') || '0x6dA53419809B352f6DE8CCA8cB29A9fAF594441b',
    licenseManager:          env('LICENSE_MANAGER_ADDRESS') || '0x851659eC0Bf800C6442DAFF5787AE2C095D65ff9',
    loyaltyProgram:          env('LOYALTY_CONTRACT_ADDRESS') || '0x3BCbfCd9D4B33017675c72908c8B71E002E803af',
    couponSystem:            env('COUPON_CONTRACT_ADDRESS') || '0x0A1466ab2E942a3fD759741cFEB313Fc228D3f5e',
    feeModuleManager:        env('FEE_MODULE_MANAGER_ADDRESS') || '0x61e35fd2F3Bd7ad314fc5C9A4a46BDd2590127Fc',
    superAdmin:              env('SUPER_ADMIN_WALLET') || '0xDe8D4055a405c84dCcF4Cc385cE176A39b652B8f',
    ecosystemRegistry:       env('ECOSYSTEM_REGISTRY_ADDRESS') || '',
  };
}

// ---------------------------------------------------------------------------
// Minimal ABIs (only the functions needed for frontend reads + writes)
// ---------------------------------------------------------------------------

const ERC20_ABI = [
  'function balanceOf(address) view returns (uint256)',
  'function allowance(address owner, address spender) view returns (uint256)',
  'function approve(address spender, uint256 amount) returns (bool)',
  'function transfer(address to, uint256 amount) returns (bool)',
  'function decimals() view returns (uint8)',
  'function symbol() view returns (string)',
  'function name() view returns (string)',
  'function totalSupply() view returns (uint256)',
  'event Transfer(address indexed from, address indexed to, uint256 value)',
  'event Approval(address indexed owner, address indexed spender, uint256 value)',
];

const EVENT_TICKET_ABI = [
  'function balanceOf(address account, uint256 id) view returns (uint256)',
  'function balanceOfBatch(address[] accounts, uint256[] ids) view returns (uint256[])',
  'function uri(uint256 tokenId) view returns (string)',
  'function isApprovedForAll(address account, address operator) view returns (bool)',
  'function setApprovalForAll(address operator, bool approved)',
  'function ticketTypes(uint256 id) view returns (uint256 price, uint256 maxSupply, uint256 minted, uint256 saleStart, uint256 saleEnd, bool paused)',
  'function isUsed(uint256 tokenId) view returns (bool)',
  'function validators(address) view returns (bool)',
  'event TransferSingle(address indexed operator, address indexed from, address indexed to, uint256 id, uint256 value)',
  'event TransferBatch(address indexed operator, address indexed from, address indexed to, uint256[] ids, uint256[] values)',
];

const EVENT_TICKET_WITH_EVENTS_ABI = [
  ...EVENT_TICKET_ABI,
  // Read — events
  'function getEvent(uint256 eventId) view returns (string name, string description, string location, string imageURI, uint256 startTime, uint256 endTime, address organizer, bool active, uint256[] ticketTypeIds, string metadataURI)',
  'function getEventCount() view returns (uint256)',
  'function getAllEventIds() view returns (uint256[])',
  'function getActiveEvents() view returns (uint256[])',
  'function getUpcomingEvents() view returns (uint256[])',
  'function getEventTags(uint256 eventId) view returns (string[])',
  'function getEventTicketTypes(uint256 eventId) view returns (uint256[])',
  // Read — ticket types
  'function getTicketType(uint256 id) view returns (uint256 eventId, string name, uint256 price, uint256 maxSupply, uint256 sold, uint256 available, uint256 saleStart, uint256 saleEnd, string metadataURI)',
  'function getTicketTypeCount() view returns (uint256)',
  // Write — event management
  'function createEvent(string name, string description, string location, string imageURI, uint256 startTime, uint256 endTime, string[] tags, string metadataURI) returns (uint256)',
  'function updateEvent(uint256 eventId, string name, string description, string location, string imageURI, uint256 startTime, uint256 endTime, string[] tags, string metadataURI)',
  'function setEventActive(uint256 eventId, bool active)',
  'function setEventManager(uint256 eventId, address manager, bool isManager)',
  // Write — ticket types
  'function addTicketType(uint256 eventId, uint256 ticketTypeId, string name, uint256 price, uint256 maxSupply, uint256 saleStart, uint256 saleEnd, uint256 transferLockStart, uint256 transferLockEnd, string metadataURI, uint256[] perks)',
  'function updateTicketTypeURI(uint256 id, string newURI)',
  // Write — tickets
  'function useTicket(uint256 tokenId)',
  'function claimPerk(uint256 tokenId, uint256 perkType)',
  // Events
  'event EventCreated(uint256 indexed eventId, string name, string location, uint256 startTime, uint256 endTime, address indexed organizer)',
  'event EventUpdated(uint256 indexed eventId)',
  'event EventStatusChanged(uint256 indexed eventId, bool active)',
  'event TicketTypeAdded(uint256 indexed id, uint256 indexed eventId, string name, uint256 price, uint256 maxSupply)',
  'event TicketMinted(address indexed owner, uint256 indexed ticketTypeId, uint256 serialNumber, uint256 tokenId)',
  'event TicketPayment(address indexed buyer, uint256 totalPricePGT)',
  'event TicketUsed(uint256 indexed tokenId, uint256 timestamp)',
  'event PerkClaimed(uint256 indexed tokenId, uint256 perkType)',
  'event ManagerUpdated(uint256 indexed eventId, address indexed manager, bool isManager)',
];

const SIMPLE_SWAP_ABI = [
  'function buyPGT(address payToken, uint256 amountIn) returns (uint256 pgtOut)',
  'function getAmountOut(address payToken, uint256 amountIn) view returns (uint256)',
  'function acceptedTokens(address) view returns (bool)',
  'event PGTBought(address indexed buyer, address indexed payToken, uint256 amountIn, uint256 pgtOut)',
];

const MARKETPLACE_ABI = [
  'function createListing(address nftContract, uint256 tokenId, uint256 amount, uint256 pricePerToken, address paymentToken, bool isAuction, uint256 endTime)',
  'function buyListing(uint256 listingId, uint256 amount)',
  'function cancelListing(uint256 listingId)',
  'function getListing(uint256 listingId) view returns (address seller, address nftContract, uint256 tokenId, uint256 amount, uint256 pricePerToken, address paymentToken, bool isAuction, uint256 endTime, bool active)',
  'event ListingCreated(uint256 indexed listingId, address indexed seller, address nftContract, uint256 tokenId, uint256 amount, uint256 pricePerToken, address paymentToken, bool isAuction, uint256 endTime)',
  'event ListingSold(uint256 indexed listingId, address indexed buyer, uint256 amount, uint256 totalPrice)',
  'event ListingCancelled(uint256 indexed listingId)',
];

const LICENSE_MANAGER_ABI = [
  'function hasLicense(address merchant, string module) view returns (bool)',
  'function getLicenseExpiry(address merchant, string module) view returns (uint256)',
  'function purchaseLicense(string module)',
  'function getModulePrice(string module) view returns (uint256)',
  'function getLicense(address) view returns (address holder, uint256 issuedAt, uint256 expiresAt, uint8 status, uint256 maxPOS, uint256 activePOS)',
  'function registerPOSTerminal(string deviceInfo) returns (uint256)',
  'function deactivatePOSTerminal(uint256 terminalId)',
  'function isPOSTerminalValid(uint256 terminalId) view returns (bool)',
  'function getMerchantTerminals(address) view returns (uint256[])',
];

const COUPON_SYSTEM_ABI = [
  'function calculateDiscount(string code, address user, uint256 purchaseAmount) view returns (uint256 discount, string reason)',
  'function redeemCoupon(string code, address user, uint256 purchaseAmount, address merchant)',
  'function createCoupon(string code, uint8 couponType, uint256 discountValue, uint256 maxUses, uint256 expiresAt, uint256 minPurchase, address merchant)',
  'function setCouponActive(string code, bool active)',
  'function getAllCoupons() view returns (string[] memory)',
  'function getMerchantCoupons(address merchant) view returns (string[] memory)',
  'function getUserHistory(address user) view returns (string[] memory)',
  'function getStatistics() view returns (uint256 totalCoupons, uint256 totalRedemptions, uint256 totalDiscountGiven)',
];

const LOYALTY_PROGRAM_ABI = [
  'function getUserLoyaltyInfo(address user) view returns (uint256 totalSpent, uint256 totalTransactions, uint256 totalRewardsEarned, uint256 referralCount, uint256 currentStreak, uint8 tier, address referrer)',
  'function getUserRewardHistory(address user) view returns (tuple(uint256 amount, uint8 ruleType, uint256 timestamp, string description)[] memory)',
  'function recordActivity(address user, uint256 spendAmount)',
  'function recordReferral(address referrer, address referred)',
  'function fundRewardPool(uint256 amount)',
  'function getRewardPoolBalance() view returns (uint256)',
];

const ECOSYSTEM_REGISTRY_ABI = [
  // Config
  'function configString(bytes32 key) view returns (string)',
  'function configUint(bytes32 key) view returns (uint256)',
  'function configAddress(bytes32 key) view returns (address)',
  'function configBool(bytes32 key) view returns (bool)',
  'function setConfigString(bytes32 key, string value)',
  'function setConfigUint(bytes32 key, uint256 value)',
  'function setConfigAddress(bytes32 key, address value)',
  'function setConfigBool(bytes32 key, bool value)',
  // Modules
  'function moduleEnabled(string module) view returns (bool)',
  'function isModuleEnabled(string module) view returns (bool)',
  'function getModuleList() view returns (string[] memory)',
  'function setModuleEnabled(string module, bool enabled)',
  // Referrals
  'function registerReferralCode(bytes32 code)',
  'function useReferralCode(bytes32 code)',
  'function getReferrer(address user) view returns (address)',
  'function getReferredUsers(address referrer) view returns (address[] memory)',
  'function referralCount(address referrer) view returns (uint256)',
  'function getAmbassadorTier(address referrer) view returns (uint256 tier, string tierName)',
  // XP
  'function getUserXP(address user) view returns (uint256 totalXP, uint256 level, uint256 streak, uint256 lastActivity)',
  'function awardXP(address user, string action)',
  'function isAchievementUnlocked(address user, string achievement) view returns (bool)',
  // Sub-admins
  'function isSubAdmin(uint256 eventId, address manager) view returns (bool)',
  'function setSubAdmin(uint256 eventId, address manager, bool permitted)',
];

// ---------------------------------------------------------------------------
// Provider / Signer management
// ---------------------------------------------------------------------------

let _provider = null;

/** Get a read-only JSON-RPC provider */
export function getProvider() {
  if (!_provider) {
    _provider = new ethers.JsonRpcProvider(getRpcUrl(), {
      chainId: getChainId(),
      name: CHAINS[getChainId()]?.name || 'Base Sepolia',
    });
  }
  return _provider;
}

/** Get a browser wallet signer (MetaMask, Coinbase Wallet, etc.) */
export async function getSigner() {
  if (typeof window === 'undefined' || !window.ethereum) {
    throw new Error('No wallet detected. Install MetaMask or Coinbase Wallet.');
  }
  const provider = new ethers.BrowserProvider(window.ethereum);
  await provider.send('eth_requestAccounts', []);
  return provider.getSigner();
}

/** Get signer from private key (for scripts/server, NEVER in browser) */
export function getWalletSigner(privateKey) {
  return new ethers.Wallet(privateKey, getProvider());
}

// ---------------------------------------------------------------------------
// Contract instances
// ---------------------------------------------------------------------------

/** PGT Token (ERC-20) */
export function pgtToken(signerOrProvider) {
  return new ethers.Contract(getAddresses().pgtToken, ERC20_ABI, signerOrProvider || getProvider());
}

/** EventTicket (ERC-1155, legacy) */
export function eventTicket(signerOrProvider) {
  return new ethers.Contract(getAddresses().eventTicket, EVENT_TICKET_ABI, signerOrProvider || getProvider());
}

/** EventTicketWithEvents (ERC-1155, production with on-chain events) */
export function eventTicketWithEvents(signerOrProvider) {
  return new ethers.Contract(getAddresses().eventTicketWithEvents, EVENT_TICKET_WITH_EVENTS_ABI, signerOrProvider || getProvider());
}

/** SimpleSwap (buy PGT with stablecoins) */
export function simpleSwap(signerOrProvider) {
  return new ethers.Contract(getAddresses().simpleSwap, SIMPLE_SWAP_ABI, signerOrProvider || getProvider());
}

/** Marketplace Router */
export function marketplace(signerOrProvider) {
  return new ethers.Contract(getAddresses().marketplace, MARKETPLACE_ABI, signerOrProvider || getProvider());
}

/** License Manager */
export function licenseManager(signerOrProvider) {
  return new ethers.Contract(getAddresses().licenseManager, LICENSE_MANAGER_ABI, signerOrProvider || getProvider());
}

/** Coupon System */
export function couponSystem(signerOrProvider) {
  return new ethers.Contract(getAddresses().couponSystem, COUPON_SYSTEM_ABI, signerOrProvider || getProvider());
}

/** Loyalty Program */
export function loyaltyProgram(signerOrProvider) {
  return new ethers.Contract(getAddresses().loyaltyProgram, LOYALTY_PROGRAM_ABI, signerOrProvider || getProvider());
}

/** Ecosystem Registry (config, referrals, XP, modules) */
export function ecosystemRegistry(signerOrProvider) {
  const addr = getAddresses().ecosystemRegistry;
  if (!addr) throw new Error('ECOSYSTEM_REGISTRY_ADDRESS not set in env');
  return new ethers.Contract(addr, ECOSYSTEM_REGISTRY_ABI, signerOrProvider || getProvider());
}

// ---------------------------------------------------------------------------
// Convenience read functions
// ---------------------------------------------------------------------------

/** Get PGT balance formatted with 2 decimals */
export async function getPGTBalance(address) {
  try {
    const contract = pgtToken();
    const raw = await contract.balanceOf(address);
    return { raw: raw.toString(), formatted: ethers.formatUnits(raw, 2) };
  } catch (err) {
    console.error('[contracts] getPGTBalance failed:', err.message);
    return { raw: '0', formatted: '0.00' };
  }
}

/** Get ETH balance */
export async function getETHBalance(address) {
  try {
    const balance = await getProvider().getBalance(address);
    return { raw: balance.toString(), formatted: ethers.formatEther(balance) };
  } catch (err) {
    console.error('[contracts] getETHBalance failed:', err.message);
    return { raw: '0', formatted: '0.0' };
  }
}

/** Check if wallet has a specific module license */
export async function hasLicense(address, moduleName) {
  try {
    const contract = licenseManager();
    return await contract.hasLicense(address, moduleName);
  } catch (err) {
    console.error('[contracts] hasLicense failed:', err.message);
    return false;
  }
}

/** Get on-chain event details from EventTicketWithEvents */
export async function getOnChainEvent(eventId) {
  const contract = eventTicketWithEvents();
  const result = await contract.getEvent(eventId);
  return {
    eventId,
    name: result.name || result[0],
    description: result.description || result[1] || '',
    location: result.location || result[2],
    imageURI: result.imageURI || result[3] || '',
    startTime: Number(result.startTime || result[4]),
    endTime: Number(result.endTime || result[5]),
    organizer: result.organizer || result[6],
    active: result.active ?? result[7],
    ticketTypeIds: (result.ticketTypeIds || result[8] || []).map(Number),
    metadataURI: result.metadataURI || result[9] || '',
  };
}

/** Get all on-chain events */
export async function getAllOnChainEvents() {
  const contract = eventTicketWithEvents();
  const ids = await contract.getAllEventIds();
  const events = await Promise.all(ids.map(id => getOnChainEvent(Number(id))));
  return events;
}

/** Get the number of events on-chain */
export async function getEventCount() {
  const contract = eventTicketWithEvents();
  return Number(await contract.getEventCount());
}

/** Create event on-chain (requires signer) */
export async function createOnChainEvent(signer, { name, description, location, imageURI, startTime, endTime, tags, metadataURI }) {
  const contract = eventTicketWithEvents(signer);
  const tx = await contract.createEvent(name, description || '', location || '', imageURI || '', startTime, endTime, tags || [], metadataURI || '');
  const receipt = await tx.wait();
  // Parse EventCreated log
  const createdLog = receipt.logs?.find(l => {
    try { return contract.interface.parseLog(l)?.name === 'EventCreated'; } catch { return false; }
  });
  const eventId = createdLog ? Number(contract.interface.parseLog(createdLog).args.eventId) : null;
  return { txHash: receipt.hash, eventId };
}

/** Update event on-chain (requires signer, must be organizer) */
export async function updateOnChainEvent(signer, eventId, { name, description, location, imageURI, startTime, endTime, tags, metadataURI }) {
  const contract = eventTicketWithEvents(signer);
  const tx = await contract.updateEvent(eventId, name, description || '', location || '', imageURI || '', startTime, endTime, tags || [], metadataURI || '');
  const receipt = await tx.wait();
  return { txHash: receipt.hash };
}

/** Set event active status on-chain */
export async function setOnChainEventActive(signer, eventId, active) {
  const contract = eventTicketWithEvents(signer);
  const tx = await contract.setEventActive(eventId, active);
  const receipt = await tx.wait();
  return { txHash: receipt.hash };
}

/** Add ticket type on-chain */
export async function addOnChainTicketType(signer, eventId, { ticketTypeId, name, price, maxSupply, saleStart, saleEnd, transferLockStart, transferLockEnd, metadataURI, perks }) {
  const contract = eventTicketWithEvents(signer);
  const tx = await contract.addTicketType(eventId, ticketTypeId, name || '', price, maxSupply, saleStart, saleEnd, transferLockStart || 0, transferLockEnd || 0, metadataURI || '', perks || []);
  const receipt = await tx.wait();
  return { txHash: receipt.hash };
}

/** Get on-chain ticket type details */
export async function getOnChainTicketType(ticketTypeId) {
  const contract = eventTicketWithEvents();
  const result = await contract.getTicketType(ticketTypeId);
  return {
    eventId: Number(result[0]),
    name: result[1],
    price: Number(result[2]),
    maxSupply: Number(result[3]),
    sold: Number(result[4]),
    available: Number(result[5]),
    saleStart: Number(result[6]),
    saleEnd: Number(result[7]),
    metadataURI: result[8] || '',
  };
}

/** Get swap quote */
export async function getSwapQuote(payToken, amountIn) {
  const contract = simpleSwap();
  const pgtOut = await contract.getAmountOut(payToken, amountIn);
  return { pgtOut: pgtOut.toString(), formatted: ethers.formatUnits(pgtOut, 2) };
}

/** Check if token is used (checked in) */
export async function isTicketUsed(tokenId) {
  const contract = eventTicketWithEvents();
  return contract.isUsed(tokenId);
}

/** Get ticket metadata URI */
export async function getTicketURI(tokenId) {
  const contract = eventTicketWithEvents();
  return contract.uri(tokenId);
}

// ---------------------------------------------------------------------------
// Coupon helpers (replaces /api/coupons)
// ---------------------------------------------------------------------------

/** Calculate discount for a coupon code */
export async function calculateCouponDiscount(code, userAddress, purchaseAmount) {
  const c = couponSystem();
  const [discount, reason] = await c.calculateDiscount(code, userAddress, purchaseAmount);
  return { discount: discount.toString(), reason };
}

/** Get all coupons */
export async function getAllCoupons() {
  const c = couponSystem();
  return c.getAllCoupons();
}

/** Get coupons for a merchant */
export async function getMerchantCoupons(merchantAddress) {
  const c = couponSystem();
  return c.getMerchantCoupons(merchantAddress);
}

/** Get coupon system stats */
export async function getCouponStats() {
  const c = couponSystem();
  const [totalCoupons, totalRedemptions, totalDiscountGiven] = await c.getStatistics();
  return { totalCoupons: Number(totalCoupons), totalRedemptions: Number(totalRedemptions), totalDiscountGiven: totalDiscountGiven.toString() };
}

// ---------------------------------------------------------------------------
// Loyalty helpers (replaces /api/loyalty)
// ---------------------------------------------------------------------------

/** Get user loyalty info */
export async function getUserLoyalty(address) {
  const c = loyaltyProgram();
  const [totalSpent, totalTransactions, totalRewardsEarned, referralCount, currentStreak, tier, referrer] = await c.getUserLoyaltyInfo(address);
  const tierNames = ['Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond'];
  return {
    totalSpent: totalSpent.toString(),
    totalTransactions: Number(totalTransactions),
    totalRewardsEarned: totalRewardsEarned.toString(),
    referralCount: Number(referralCount),
    currentStreak: Number(currentStreak),
    tier: Number(tier),
    tierName: tierNames[Number(tier)] || 'Bronze',
    referrer,
  };
}

/** Get reward pool balance */
export async function getRewardPoolBalance() {
  const c = loyaltyProgram();
  const bal = await c.getRewardPoolBalance();
  return { raw: bal.toString(), formatted: ethers.formatUnits(bal, 2) };
}

// ---------------------------------------------------------------------------
// License helpers (replaces /api/license)
// ---------------------------------------------------------------------------

/** Get full license details */
export async function getLicenseDetails(address) {
  const c = licenseManager();
  const [holder, issuedAt, expiresAt, status, maxPOS, activePOS] = await c.getLicense(address);
  return {
    holder,
    issuedAt: Number(issuedAt),
    expiresAt: Number(expiresAt),
    status: Number(status),
    statusText: ['None', 'Active', 'Expired', 'Revoked'][Number(status)] || 'Unknown',
    maxPOS: Number(maxPOS),
    activePOS: Number(activePOS),
  };
}

/** Get merchant POS terminals */
export async function getMerchantTerminals(address) {
  const c = licenseManager();
  return c.getMerchantTerminals(address);
}

// ---------------------------------------------------------------------------
// Registry helpers (replaces /api/config, /api/referral, /api/xp, /api/modules)
// ---------------------------------------------------------------------------

/** Get a string config value from the registry */
export async function getRegistryConfig(key) {
  const c = ecosystemRegistry();
  const keyHash = ethers.id(key); // keccak256
  return c.configString(keyHash);
}

/** Check if a module is enabled */
export async function isModuleEnabled(moduleName) {
  const c = ecosystemRegistry();
  return c.isModuleEnabled(moduleName);
}

/** Get user XP and level */
export async function getUserXP(address) {
  const c = ecosystemRegistry();
  const [totalXP, level, streak, lastActivity] = await c.getUserXP(address);
  return { totalXP: Number(totalXP), level: Number(level), streak: Number(streak), lastActivity: Number(lastActivity) };
}

/** Get ambassador tier */
export async function getAmbassadorTier(address) {
  const c = ecosystemRegistry();
  const [tier, tierName] = await c.getAmbassadorTier(address);
  return { tier: Number(tier), tierName };
}

/** Get referral count */
export async function getReferralCount(address) {
  const c = ecosystemRegistry();
  return Number(await c.referralCount(address));
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export default {
  // Config
  getAddresses,
  getChainId,
  getRpcUrl: () => getRpcUrl(),
  // Provider
  getProvider,
  getSigner,
  getWalletSigner,
  // Contracts
  pgtToken,
  eventTicket,
  eventTicketWithEvents,
  simpleSwap,
  marketplace,
  licenseManager,
  couponSystem,
  loyaltyProgram,
  ecosystemRegistry,
  // Read helpers — Token
  getPGTBalance,
  getETHBalance,
  // Read helpers — Events/Tickets
  getOnChainEvent,
  getAllOnChainEvents,
  getEventCount,
  getOnChainTicketType,
  isTicketUsed,
  getTicketURI,
  // Write helpers — Events/Tickets
  createOnChainEvent,
  updateOnChainEvent,
  setOnChainEventActive,
  addOnChainTicketType,
  // Read helpers — Swap
  getSwapQuote,
  // Read helpers — Coupons (replaces /api/coupons)
  calculateCouponDiscount,
  getAllCoupons,
  getMerchantCoupons,
  getCouponStats,
  // Read helpers — Loyalty (replaces /api/loyalty)
  getUserLoyalty,
  getRewardPoolBalance,
  // Read helpers — License (replaces /api/license)
  hasLicense,
  getLicenseDetails,
  getMerchantTerminals,
  // Read helpers — Registry (replaces /api/config, /api/xp, /api/referral, /api/modules)
  getRegistryConfig,
  isModuleEnabled,
  getUserXP,
  getAmbassadorTier,
  getReferralCount,
};
